
import React, { useState } from 'react';
import { Company } from '../types';
import { GlobeIcon, TargetIcon, PhoneIcon, ChevronDownIcon, SearchIcon, MailIcon, BookIcon } from './Icons';
import { SourceBadge } from './SourceBadge';

// Trash icon for delete
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

interface UnifiedLaneProps {
  result: {
    agentId: string;
    data: {
      lead?: any,
      research?: any,
      extraResearch?: any,
      strategy?: any
    },
    isDeepResearch?: boolean
  };
  onDeepResearch: (lead: any, mode: 'deep' | 'extra') => void;
  onGenerateStrategy: (research: any) => void;
  onSaveLead: (_data: any) => void;
  onDeleteLead: (target: { companyName: string; companyId?: string }) => void;
  onStartCall: (company: Company, strategy?: any) => void;
  onCompanySelect: (company: Company) => void;
  defaultCollapsed?: boolean;
  disableCallAgent?: boolean;
}

export const ResultViewer: React.FC<UnifiedLaneProps> = ({
  result,
  onDeepResearch,
  onGenerateStrategy,
  onSaveLead,
  onDeleteLead,
  onStartCall,
  onCompanySelect,
  defaultCollapsed = true,
  disableCallAgent
}) => {
  const { lead, research, extraResearch, strategy } = result.data;
  const isDeep = result.isDeepResearch || research?.isDeepResearch;
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const companyName = research?.companyName || lead?.companyName || extraResearch?.companyName || lead?.name || "Onbekend Bedrijf";
  const resolvedCompanyId = lead?.companyId || research?.companyId || extraResearch?.companyId || strategy?.companyId || lead?.id || research?.id;

  const buildCompanyPayload = (): Company => ({
    id: resolvedCompanyId || crypto.randomUUID(),
    name: companyName,
    industry: research?.industry || lead?.industry || 'Lead',
    description: research?.summary || lead?.summary || lead?.reason || '',
    website: research?.contactInfo?.website || lead?.contactInfo?.website || lead?.website || '',
    location: research?.contactInfo?.address || lead?.contactInfo?.address || lead?.location || '',
    email: research?.contactInfo?.email || lead?.contactInfo?.email || lead?.email || '',
    phone: research?.contactInfo?.phone || lead?.contactInfo?.phone || lead?.phone || '',
    contactName: research?.contactInfo?.keyContact || lead?.contactName
  });

  // Determine status for the badge
  const getStatus = () => {
    if (strategy) return { label: 'Strategie', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50' };
    if (research) return { label: 'Onderzocht', color: 'bg-indigo-500', textColor: 'text-indigo-700', bgColor: 'bg-indigo-50' };
    return { label: 'Lead', color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50' };
  };
  const status = getStatus();
  const companyPayload = buildCompanyPayload();
  const companyDescription = (lead?.description || companyPayload.description || '').trim();
  const leadSummary = lead?.reason || lead?.summary || companyDescription || 'Nog geen samenvatting beschikbaar.';
  const leadNextStep = lead?.suggestedNextStep || 'Start met verdiepend onderzoek.';
  const researchSummary = research?.summary || '';
  const insightItems = (research?.deepInsights || research?.keyPitchPoints || []).slice(0, 5);
  const commercialCards = [
    research?.idealBuyer ? { label: 'Ideale Koper', value: research.idealBuyer } : null,
    research?.salesHook ? { label: 'Saleshaak', value: research.salesHook } : null,
    research?.recommendedChannel ? { label: 'Kanaal', value: research.recommendedChannel } : null,
    research?.confidence ? { label: 'Confidence', value: research.confidence } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;
  const painSignalItems = (research?.painSignals || []).slice(0, 3);
  const timingSignalItems = (research?.timingSignals || []).slice(0, 3);
  const normalizedLeadSummary = leadSummary.trim().toLowerCase();
  const normalizedResearchSummary = researchSummary.trim().toLowerCase();
  const showResearchSummaryBlock = !!researchSummary && normalizedResearchSummary !== normalizedLeadSummary;
  const genericLeadCopy = [
    'relevant bedrijf voor dit project.',
    'relevant bedrijf uit de interne dataset.',
    'nog geen samenvatting beschikbaar.',
  ];
  const stripWrappingQuotes = (text: string) => text.replace(/^["'“”]+|["'“”]+$/g, '').trim();
  const isGenericLeadSummary = genericLeadCopy.includes(normalizedLeadSummary);
  const firstCompanySentence = companyDescription
    .split(/(?<=[.!?])\s+/)
    .find((sentence: string) => sentence.trim().length > 0)
    ?.trim();
  const whatTheyDo = firstCompanySentence
    || (lead?.industry && lead.industry !== 'Lead'
      ? `${companyName} opereert als ${lead.industry.toLowerCase()}${lead?.location ? ` in ${lead.location}` : ''}.`
      : `${companyName} lijkt inhoudelijk aan te sluiten op dit project.`);
  const qualificationQuote = isGenericLeadSummary
    ? `${whatTheyDo} Dit lijkt een relevante lead voor verdere kwalificatie.`
    : stripWrappingQuotes(leadSummary);
  const qualificationLogic = lead?.qualificationLogic
    || `${whatTheyDo} ${isGenericLeadSummary ? 'De fit moet vooral bevestigd worden met inhoudelijk onderzoek en concrete contactsignalen.' : 'De leadfit volgt uit de zichtbare match tussen hun propositie en de projectdoelstelling.'}`.replace(/\s+/g, ' ').trim();
  const buyerExpectation = lead?.buyerExpectation
    || (/booking|entertainment|events|festival|bruiloft|artiest|optreden|muziek/i.test(`${lead?.industry || ''} ${companyDescription} ${leadSummary}`)
      ? 'Verwacht waarschijnlijk een professionele aanmelding, sterk repertoire en overtuigend portfolio-materiaal.'
      : 'Verwacht waarschijnlijk een korte, duidelijke propositie met directe relevantie voor hun business.');

  const renderLeadQualificationCard = (centered = false) => (
    <div className={`bg-indigo-50/55 border border-indigo-100 p-6 rounded-[2.2rem] ${centered ? 'max-w-[36rem] mx-auto text-center' : ''}`}>
      <div className={`mb-4 inline-flex bg-white px-4 py-1 rounded-full border border-indigo-100 text-[9px] font-black text-indigo-600 uppercase tracking-widest ${centered ? 'mx-auto' : ''}`}>
        Lead Kwalificatie
      </div>
      <p className="text-[13px] text-indigo-950 font-bold leading-[1.75] break-words">"{qualificationQuote}"</p>
      <div className={`mt-5 grid gap-3 ${centered ? 'text-left' : ''}`}>
        <div className="rounded-[1.4rem] border border-indigo-100/70 bg-white/70 px-4 py-3">
          <p className="text-[9px] font-black uppercase tracking-[0.22em] text-indigo-500">Wat ze doen / waarom logisch</p>
          <p className="mt-2 text-[10.5px] leading-[1.7] font-semibold text-slate-700 break-words">{qualificationLogic}</p>
        </div>
        <div className="rounded-[1.4rem] border border-indigo-100/70 bg-white/70 px-4 py-3">
          <p className="text-[9px] font-black uppercase tracking-[0.22em] text-indigo-500">Waarschijnlijk Verwacht</p>
          <p className="mt-2 text-[10.5px] leading-[1.7] font-semibold text-slate-700 break-words">{buyerExpectation}</p>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-indigo-100/60 text-[10px] text-indigo-500 font-black uppercase tracking-[0.16em] leading-relaxed">
        Volgende stap: {leadNextStep}
      </div>
    </div>
  );

  const renderContactItem = (label: string, value?: string, icon?: any) => {
    if (!value) return null;
    return (
      <div className="min-w-0 max-w-full flex items-center gap-2 text-[10px] font-bold text-slate-500 bg-white border border-slate-100 px-3 py-1.5 rounded-xl shadow-sm">
        <span className="text-indigo-400">{icon}</span>
        <span className="min-w-0 max-w-[220px] break-words [overflow-wrap:anywhere]">{value}</span>
      </div>
    );
  };

  // ──────────────────────────────────────────────
  // COLLAPSED VIEW — Compact Thumb Card
  // ──────────────────────────────────────────────
  if (isCollapsed) {
    return (
      <div
        className={`bg-white rounded-2xl border shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300 transition-all cursor-pointer hover:shadow-md group ${
          isDeep ? 'border-indigo-300 hover:border-indigo-400' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <div className="flex items-center gap-4 px-6 py-4" onClick={() => setIsCollapsed(false)}>
          {/* Avatar */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg shrink-0 transition-transform group-hover:scale-110 ${
            isDeep ? 'bg-indigo-50 text-indigo-600 border border-indigo-200' : 'bg-slate-50 text-indigo-400 border border-slate-200'
          }`}>
            {companyName[0]}
          </div>

          {/* Company Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-black text-slate-900 truncate">{companyName}</h3>
              {lead?.source && <SourceBadge source={lead.source} />}
            </div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest truncate">
              {research?.industry || lead?.industry || 'B2B Lead'}
            </p>
          </div>

          {/* Status Badge */}
          <div className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${status.textColor} ${status.bgColor} border border-current/10 shrink-0`}>
            {status.label}
          </div>

          {/* Quick Action */}
          <div className="flex items-center gap-2 shrink-0">
            {strategy && !disableCallAgent && (
              <button
                onClick={(e) => { e.stopPropagation(); onStartCall(buildCompanyPayload(), strategy); }}
                className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-xl transition-colors"
                title="Start Bel Actie"
              >
                <PhoneIcon />
              </button>
            )}
            {!research && !strategy && (
              <button
                onClick={(e) => { e.stopPropagation(); onDeepResearch(lead, 'deep'); }}
                className="p-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-xl transition-colors"
                title="Diepte Onderzoek"
              >
                <SearchIcon />
              </button>
            )}
            {research && !strategy && (
              <button
                onClick={(e) => { e.stopPropagation(); onGenerateStrategy(research); }}
                className="p-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-xl transition-colors"
                title="Strategie Bepalen"
              >
                <TargetIcon />
              </button>
            )}

            {/* Delete */}
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteLead({ companyName, companyId: lead?.companyId || research?.companyId || extraResearch?.companyId || strategy?.companyId }); }}
              className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              title="Verwijder lead"
            >
              <TrashIcon />
            </button>

            {/* Expand Chevron */}
            <div className="text-slate-300 group-hover:text-indigo-400 transition-colors">
              <ChevronDownIcon />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────
  // EXPANDED VIEW — Full Detail Card
  // ──────────────────────────────────────────────
  return (
    <div className={`bg-white rounded-[2.5rem] border shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 transition-all group ${isDeep
        ? 'border-indigo-400 ring-4 ring-indigo-50/50'
        : 'border-slate-200 hover:border-slate-300 hover:shadow-lg'
      }`}>
      {/* 1. Header Lane: Identiteit & Globale Acties */}
      <div className={`px-6 py-5 xl:px-7 xl:py-6 flex flex-wrap items-center justify-between gap-5 border-b transition-colors ${isDeep ? 'bg-indigo-50/20 border-indigo-100' : 'bg-slate-50/30 border-slate-100'
        }`}>
        <div className="flex items-center gap-6 cursor-pointer" onClick={() => onCompanySelect(buildCompanyPayload())}>
          <div className={`w-14 h-14 bg-white border rounded-2xl flex items-center justify-center font-black text-2xl shadow-inner transition-all group-hover:scale-105 ${isDeep ? 'border-indigo-200 text-indigo-600 shadow-indigo-100' : 'border-slate-200 text-indigo-400'
            }`}>
            {companyName[0]}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                {companyName}
              </h3>
              {isDeep && <span className="bg-indigo-600 text-white text-[9px] px-3 py-1 rounded-full uppercase tracking-[0.2em] font-black animate-pulse shadow-lg shadow-indigo-600/20">Diepe Intelligentie</span>}
              {lead?.source && <SourceBadge source={lead.source} />}
            </div>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mt-0.5">
              {research?.industry || lead?.industry || "B2B Analyse Node"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Collapse button */}
          <button
            onClick={() => setIsCollapsed(true)}
            className="flex items-center gap-2 px-3 py-3 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-2xl transition-all border border-transparent hover:border-indigo-100 text-[10px] font-bold uppercase tracking-widest"
            title="Inklappen"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          </button>

          {/* Delete */}
          <button
            onClick={() => onDeleteLead({ companyName, companyId: lead?.companyId || research?.companyId || extraResearch?.companyId || strategy?.companyId })}
            className="flex items-center gap-2 px-3 py-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all border border-transparent hover:border-red-100 text-[10px] font-bold uppercase tracking-widest"
            title="Verwijder lead"
          >
            <TrashIcon />
          </button>

          <div className="h-10 w-px bg-slate-200 mx-1 hidden sm:block"></div>

          <button
            onClick={() => onDeepResearch(lead || companyPayload, 'deep')}
            className="px-6 py-3 rounded-[1.2rem] text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-3 transition-all shadow-xl hover:-translate-y-1 active:scale-95 bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
          >
            <SearchIcon /> Deep Dive
          </button>

          <button
            onClick={() => onGenerateStrategy(research || { companyId: resolvedCompanyId, companyName: companyName })}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-[1.2rem] text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-3 transition-all shadow-xl shadow-indigo-600/20 hover:-translate-y-1 active:scale-95"
          >
            <TargetIcon /> Strategie
          </button>

          {!disableCallAgent && (
            <button
              onClick={() => onStartCall(buildCompanyPayload(), strategy)}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-[1.2rem] text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-3 transition-all shadow-xl shadow-emerald-600/20 hover:-translate-y-1 active:scale-95"
            >
              <PhoneIcon /> Bel Actie
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.28fr)_minmax(340px,0.92fr)] min-h-[300px]">
        {/* 2. Intelligence Column (Lead + Research) */}
        <div className={`min-w-0 px-6 py-6 xl:px-7 xl:py-7 flex flex-col ${strategy || extraResearch ? 'border-r border-slate-100' : ''}`}>
          {research ? (
            <div className="space-y-8 flex-1">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(320px,0.88fr)_minmax(0,1.36fr)]">
                <div className="min-w-0 space-y-4">
                  {renderLeadQualificationCard()}

                  {showResearchSummaryBlock && (
                    <div className="bg-slate-50/70 border border-slate-100 p-5 rounded-[1.6rem] shadow-inner">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-2">
                        <BookIcon /> Onderzoek Snapshot
                      </h4>
                      <p className="mt-3 text-[13px] text-slate-600 leading-[1.75] whitespace-pre-wrap font-medium break-words [overflow-wrap:anywhere]">
                        {researchSummary}
                      </p>
                    </div>
                  )}
                </div>

                <div className="min-w-0 space-y-5">
                  <h4 className={`text-[10px] font-black uppercase tracking-[0.3em] flex items-center gap-2 ${isDeep ? 'text-indigo-600' : 'text-slate-400'}`}>
                    <TargetIcon /> Diepte Inzichten
                  </h4>
                  {commercialCards.length > 0 && (
                    <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                      {commercialCards.map((item) => (
                        <div key={item.label} className="min-w-0 rounded-[1.3rem] border border-slate-100 bg-slate-50/70 px-4 py-3">
                          <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-400">{item.label}</p>
                          <p className="mt-2 text-[10.5px] leading-[1.6] font-bold text-slate-700 break-words [overflow-wrap:anywhere]">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {insightItems.length > 0 ? (
                    <ul className="space-y-4">
                      {insightItems.map((insight: string, i: number) => (
                        <li key={i} className="min-w-0 text-[10.5px] text-slate-600 flex gap-4 leading-[1.65] font-medium break-words [overflow-wrap:anywhere]">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shadow-[0_0_8px_rgba(79,70,229,0.5)] shrink-0"></span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="bg-slate-50/70 border border-slate-100 p-5 rounded-[1.6rem] text-[13px] text-slate-600 leading-[1.75] font-medium break-words [overflow-wrap:anywhere]">
                      {researchSummary || 'Nog geen extra inzichten beschikbaar.'}
                    </div>
                  )}

                  {(painSignalItems.length > 0 || timingSignalItems.length > 0) && (
                    <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                      {painSignalItems.length > 0 && (
                        <div className="min-w-0 rounded-[1.5rem] border border-rose-100 bg-rose-50/50 px-4 py-4">
                          <p className="text-[9px] font-black uppercase tracking-[0.25em] text-rose-500">Pijnsignalen</p>
                          <ul className="mt-3 space-y-2">
                            {painSignalItems.map((signal: string, index: number) => (
                              <li key={`${signal}-${index}`} className="text-[10.5px] leading-[1.6] text-slate-700 font-medium break-words [overflow-wrap:anywhere]">
                                {signal}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {timingSignalItems.length > 0 && (
                        <div className="min-w-0 rounded-[1.5rem] border border-emerald-100 bg-emerald-50/50 px-4 py-4">
                          <p className="text-[9px] font-black uppercase tracking-[0.25em] text-emerald-600">Timing Signalen</p>
                          <ul className="mt-3 space-y-2">
                            {timingSignalItems.map((signal: string, index: number) => (
                              <li key={`${signal}-${index}`} className="text-[10.5px] leading-[1.6] text-slate-700 font-medium break-words [overflow-wrap:anywhere]">
                                {signal}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {extraResearch && (
                <div className="mt-6 pt-6 border-t border-slate-100 space-y-5">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-2">
                    <i className="fas fa-star text-amber-400"></i> Markt Sentiment & Reputatie
                  </h4>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 bg-amber-50/30 p-5 rounded-[1.8rem] border border-amber-100/50">
                    <div className="space-y-2">
                      <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Sentiment Core</p>
                      <p className="text-[12px] text-slate-700 font-bold italic break-words [overflow-wrap:anywhere]">"{extraResearch.sentiment}"</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Markt Positie</p>
                      <p className="text-[12px] text-slate-700 font-medium leading-[1.65] break-words [overflow-wrap:anywhere]">{extraResearch.marketReputation}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-6 mt-auto border-t border-slate-50 flex flex-wrap gap-3">
                {renderContactItem("Web", research.contactInfo?.website || lead?.website, <GlobeIcon />)}
                {renderContactItem("E-mail", research.contactInfo?.email || lead?.email, <MailIcon />)}
                {renderContactItem("Tel", research.contactInfo?.phone || lead?.phone, <PhoneIcon />)}
                {renderContactItem("Locatie", research.contactInfo?.address || lead?.location, <i className="fas fa-map-marker-alt"></i>)}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-center items-center py-12 px-6">
              {lead && (
                <div className="max-w-[40rem] w-full text-center space-y-6">
                  {renderLeadQualificationCard(true)}

                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.4em] animate-pulse">Wachten op Diepte Onderzoek...</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. Strategy Column */}
        {strategy ? (
          <div className="min-w-0 px-6 py-6 xl:px-7 xl:py-7 bg-slate-50/30 flex flex-col border-l border-slate-100">
            <div className="space-y-7 flex-1">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-sm shadow-lg shadow-indigo-600/20">
                  <TargetIcon />
                </div>
                <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-[0.3em]">Strategisch Plan</h4>
              </div>

              <div className="space-y-5">
                <div className="bg-white p-6 rounded-[1.8rem] border border-slate-200 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-600"></div>
                  <p className="text-[9px] font-black text-indigo-600 uppercase tracking-[0.22em] mb-3 break-words [overflow-wrap:anywhere]">{strategy.strategyTitle}</p>
                  <p className="text-[13px] text-slate-700 italic leading-[1.75] font-bold break-words [overflow-wrap:anywhere]">"{strategy.approachAngle}"</p>
                </div>

                <div className="space-y-3">
                  <h5 className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">Key Value Anchors</h5>
                  <div className="grid grid-cols-1 gap-2">
                    {strategy.keyPitchPoints?.slice(0, 3).map((point: string, idx: number) => (
                      <div key={idx} className="bg-indigo-50 text-indigo-700 text-[10px] leading-[1.45] font-bold px-3 py-2 rounded-xl border border-indigo-100 break-words [overflow-wrap:anywhere]">
                        {point}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-100 flex justify-between items-center">
              <button
                onClick={() => onCompanySelect(buildCompanyPayload())}
                className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] hover:text-indigo-600 transition-colors flex items-center gap-3"
              >
                Open Dossier <ChevronDownIcon />
              </button>
            </div>
          </div>
        ) : research ? (
          <div className="p-10 lg:col-span-5 flex flex-col justify-center items-center bg-slate-50/20 border-l border-slate-100">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center border border-slate-200 shadow-sm mx-auto text-slate-300">
                <TargetIcon />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Strategie Engine Klaar</p>
                <p className="text-xs text-slate-500 max-w-[200px] mx-auto leading-relaxed">Synthetiseer een benaderingsplan op basis van de gevonden data.</p>
              </div>
              <button
                onClick={() => onGenerateStrategy(research)}
                className="px-6 py-2.5 bg-white border border-slate-200 text-slate-900 hover:text-indigo-600 hover:border-indigo-300 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] shadow-sm transition-all active:scale-95"
              >
                Start Strategie Analyse
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
